import { ReactNode } from "react";
import { Button, MenuItem, Select, Stack, Typography } from "@mui/material";
import { Control, Controller, useFieldArray, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FoodsIcon } from "@foodpilot/foods";
import { FormProductSelector } from "src/components/Selector/ProductSelector/FormProductSelector";
import { FormIngredientSelector } from "src/components/Selector/IngredientSelector/FormIngredientSelector";
import { FormPlatformIngredientReferenceSelector } from "src/components/Selector/IngredientReferenceSelector/FormPlatformIngredientReferenceSelector";
import {
    DataListExtraOptionForm,
    DataListOptionValueOverrideForm,
    DataListValueOverrideFormData,
} from "./DataListValueOverrideModal";

type DataListValueOverrideFieldsProps = {
    property: "dataListOptionValueOverrides" | "dataListExtraOptions";
    control: Control<DataListValueOverrideFormData>;
    watch: UseFormWatch<DataListValueOverrideFormData>;
    getAdditionalFields: (index: number) => ReactNode;
    defaultValue: DataListOptionValueOverrideForm | DataListExtraOptionForm;
};
export const DataListValueOverrideFields = (props: DataListValueOverrideFieldsProps) => {
    const { property, control, watch, getAdditionalFields, defaultValue } = props;
    const { t } = useTranslation();

    const { fields, append, remove } = useFieldArray({
        control,
        name: property,
    });

    const watchedDataList = watch("dataList");

    return (
        <Stack spacing="10px">
            {fields.map((field, index) => {
                const watchedType = watch(`${property}.${index}.type`);
                const optionAdditionalFields = getAdditionalFields(index);

                return (
                    <Stack
                        key={field.id}
                        sx={{
                            border: "1px solid #DDD",
                            borderRadius: "5px",
                            padding: "12px 16px",
                            position: "relative",
                        }}
                        gap="16px"
                    >
                        {optionAdditionalFields}
                        <Stack sx={{ width: "50%" }}>
                            <Typography
                                variant="h6"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                            >
                                {t(
                                    "settings.data_lists_overrides.data_list.option.replace_value_with",
                                    "Replace value with",
                                )}
                                * :
                            </Typography>
                            <Controller
                                name={`${property}.${index}.type`}
                                control={control}
                                render={({ field: typeField }) => {
                                    return (
                                        <Select
                                            value={typeField.value}
                                            onChange={(e) => {
                                                typeField.onChange(e.target.value);
                                            }}
                                            sx={{ height: "48px" }}
                                        >
                                            <MenuItem value={"entity_score"}>
                                                {t(
                                                    "settings.data_lists_overrides.data_list.option.type.entity_score",
                                                    "Entity score",
                                                )}
                                            </MenuItem>
                                        </Select>
                                    );
                                }}
                            />
                        </Stack>
                        {watchedType === "entity_score" && (
                            <Stack sx={{ width: "50%" }}>
                                <Controller
                                    name={`${property}.${index}.context`}
                                    control={control}
                                    render={({ field: contextField }) => {
                                        const contextValue = contextField.value || {};

                                        return (
                                            <>
                                                <Stack sx={{ width: "50%" }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                            marginBottom: "8px",
                                                        }}
                                                    >
                                                        {t(
                                                            "settings.data_lists_overrides.data_list.option.type.entity_score.entity_type",
                                                            "Entity type",
                                                        )}
                                                        * :
                                                    </Typography>
                                                    <Select
                                                        value={contextValue.entity ?? ""}
                                                        onChange={(e) => {
                                                            contextField.onChange({
                                                                entity: e.target.value,
                                                            });
                                                        }}
                                                        sx={{ height: "48px" }}
                                                    >
                                                        <MenuItem value={"product"}>{t("product", "Product")}</MenuItem>
                                                        <MenuItem value={"intermediate_product"}>
                                                            {t("intermediate_product", "Intermediate product")}
                                                        </MenuItem>
                                                        <MenuItem value={"ingredient"}>
                                                            {t("ingredient", "Ingredient")}
                                                        </MenuItem>
                                                        <MenuItem value={"reference_ingredient"}>
                                                            {t(
                                                                "settings.data_lists_overrides.data_list.option.type.entity_score.entity_type.reference_ingredient",
                                                                "Platform reference ingredient",
                                                            )}
                                                        </MenuItem>
                                                    </Select>
                                                </Stack>

                                                {contextValue.entity && (
                                                    <Stack sx={{ width: "50%", marginTop: "8px" }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                lineHeight: "1.6em",
                                                                color: "#393A36",
                                                                marginBottom: "16px",
                                                            }}
                                                        >
                                                            {t(
                                                                "settings.data_lists_overrides.data_list.option.type.entity_score.entity",
                                                                "Entity",
                                                            )}
                                                            * :
                                                        </Typography>
                                                        {contextValue.entity !== "ingredient" &&
                                                            contextValue.entity !== "reference_ingredient" && (
                                                                <FormProductSelector
                                                                    value={contextValue.id?.toString()}
                                                                    onChange={(productId) => {
                                                                        contextField.onChange({
                                                                            ...contextValue,
                                                                            id: parseInt(productId),
                                                                        });
                                                                    }}
                                                                    intermediateProducts={
                                                                        contextValue.entity === "intermediate_product"
                                                                    }
                                                                />
                                                            )}
                                                        {contextValue.entity === "ingredient" && (
                                                            <FormIngredientSelector
                                                                value={contextValue.id?.toString()}
                                                                onChange={(ingredientId) => {
                                                                    contextField.onChange({
                                                                        ...contextValue,
                                                                        id: parseInt(ingredientId),
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                        {contextValue.entity === "reference_ingredient" && (
                                                            <FormPlatformIngredientReferenceSelector
                                                                value={contextValue.id?.toString() ?? ""}
                                                                onChange={(referenceId) => {
                                                                    contextField.onChange({
                                                                        ...contextValue,
                                                                        id: parseInt(referenceId),
                                                                    });
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                )}
                                            </>
                                        );
                                    }}
                                />
                            </Stack>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<FoodsIcon icon="delete" size={2} />}
                            onClick={() => remove(index)}
                            sx={{ position: "absolute", right: "12px", border: "none !important" }}
                        >
                            {t("delete", "Delete")}
                        </Button>
                    </Stack>
                );
            })}

            <Button
                variant="outlined"
                startIcon={<FoodsIcon icon="add" size={2} />}
                onClick={() => append(defaultValue)}
                sx={{ alignSelf: "flex-start", border: "none !important" }}
                disabled={watchedDataList === undefined}
            >
                {t("add", "Add")}
            </Button>
        </Stack>
    );
};
